import React, { Fragment } from 'react';
import { resolve } from 'library/helpers/path-resolver';
import { Switch, Route, Link, Redirect } from 'library/util/core-router';
import PropTypes from 'prop-types';
import { ShimmyValue } from 'library/components/shimmy';
import Logo from 'apps/hiring/components/logo';
import { ModalComponent as Modal } from 'library/helpers/modal';
import AddOpeningModalBody from 'apps/hiring/components/add-opening';
import HiringQuery from 'apps/hiring/util/query';
import withData from 'util/hoc/with-data';
import withConfig from 'util/hoc/with-config';
import SectionSearchAndFilter from 'apps/hiring/components/section-search-filter';
import { fuzzy_search } from 'library/helpers/text';
import MastheadTitle from 'apps/hiring/components/masthead-title';
import Env from 'library/util/env';

import PlusSvg from 'library/icons/plus.svg';
import ExploreSvg from 'library/icons/explore.svg';

const HiringWelcome = ({ app, scrollPos=0 }) => (
  <div className="hiring-header-welcome">
    <h1>
      <span>Welcome to PAIRIN Hiring</span>
    </h1>
  </div>
);

const CreateCard = ({ children, openingCount=0, onClick }) => (
  <div className={['hiring-app-card',openingCount==0?'hiring-app-card-alt':null].filter(c=>c).join(' ')}>
    <div onClick={onClick} className="hiring-app-card-body">
      <PlusSvg width="40px" height="40px" />
      Add Opening
    </div>
    { children }
  </div>
);

const CreateOpening = ({ openingCount=0, onClick=()=>{}, canViewDemo=false }) => (
  openingCount > 0
    ? (
      <CreateCard onClick={onClick} openingCount={openingCount}>
        {
          canViewDemo
            ? (
              <div className="hiring-app-card-footer hiring-app-card-alt">
                <ExploreSvg width="40px" height="40px" />
                Explore Demo Project
              </div>
            )
            : null
        }
      </CreateCard>
    )
    : (
      <Fragment>
        <CreateCard onClick={onClick} openingCount={openingCount} />
        {
          canViewDemo
            ? (
              <div className="hiring-app-card">
                <div className="hiring-app-card-body">
                  <ExploreSvg width="40px" height="40px" />
                  Explore Demo Project
                </div>
              </div>
            )
            : null
        }
      </Fragment>
    )
);

const OpeningsLoading = () => (
  <Fragment>
    {
      (new Array(Math.floor(Math.max(1, Math.min(1200, window.innerWidth) / 300)))).fill(null).map((v,i)=>(
        <div key={i} className="hiring-app-card hiring-app-card-loading" />
      ))
    }
  </Fragment>
);

class Openings extends React.Component {
  state = {
    addOpening: false,
    redirect: null,
    search: '',
    filter: 'open:true'
  }

  static defaultProps = {
    subscription: {}
  }

  closeAddOpening = () => {
    this.setState({
      addOpening: false
    });
  }

  addOpening = () => {
    this.setState({
      addOpening: true
    });
  }

  handleOpeningAdded = (opening_id) => {
    this.setState({
      redirect: `/hiring/openings/${opening_id}/evaluate`
    });
  }

  mapOpening = ({ id, name, application_count, settings, open=true }) => (
    <Link to={`/hiring/openings/${id}/evaluate`} className={['hiring-app-card', !open?'closed':''].filter(c=>c).join(' ')}>
      <div className="hiring-app-card-body">
        <div className="hiring-app-card-header">
          { name }
        </div>
        <div className="hiring-app-card-inner">
          { application_count } <small>APPLICANTS</small>
        </div>
        <div className="hiring-app-card-link">
          <small>SURVEY LINK</small>
          {Env.get('__SURVEYURL__', 'https://survey.pairin.com')}/apply/{settings.invitation_token}
        </div>
      </div>
    </Link>
  )

  handleChange = (e) => {
    let { value, name, type } = e.target;

    if (['radio', 'checkbox'].indexOf(type) > -1) {
      value = !resolve(this.state, name, false);
    }

    this.setState({
      [name]: value
    });
  }

  filterOpenings = ({ name }) => (
    fuzzy_search(this.state.search.toLowerCase(), name.toLowerCase())
  )

  get can_view_demo() {
    return resolve(this.props, 'configuration/apps/hiring/configuration/can_view_demo', false);
  }

  render() {
    const {
      match,
      graph_data,
      graph_loading,
      subscription
    } = this.props;

    const {
      redirect,
      show_closed
    } = this.state;

    if (redirect) {
      return <Redirect to={redirect} />;
    }

    const data = [...resolve(graph_data, 'openings', []), ...(show_closed?resolve(graph_data, 'closedOpenings', []):[])];

    const filtered = data.filter(this.filterOpenings);
    return (
      <Fragment>
        <ShimmyValue namespace="hiring-navigation-logo-text">
          <MastheadTitle subscription={subscription} title={'Openings'} />
        </ShimmyValue>
        <ShimmyValue namespace="hiring-breadcrumbs">
          <span>Openings</span>
        </ShimmyValue>
        <SectionSearchAndFilter search={this.state.search} onChange={this.handleChange}>
          <label>
            <input type="checkbox" checked={show_closed} onChange={this.handleChange} name="show_closed" />
            Show Closed
          </label>
        </SectionSearchAndFilter>
        <section className="hiring-app-section">
          {
            graph_loading
              ? <OpeningsLoading />
              : (
                <Fragment>
                  <CreateOpening
                    canViewDemo={this.can_view_demo}
                    onClick={this.addOpening}
                    openingCount={resolve(graph_data, 'openings', []).length}
                  />
                  {filtered.map(this.mapOpening)}
                  {
                    filtered.length < data.length
                      ? (
                        <div className="hiring-app-card-info">
                          { data.length - filtered.length } hidden by search
                        </div>
                      )
                      : null
                  }
                  <Modal onClose={this.closeAddOpening} active={this.state.addOpening}>
                    <AddOpeningModalBody onComplete={this.handleOpeningAdded} onClose={this.closeAddOpening} />
                  </Modal>
                </Fragment>
              )
          }
        </section>
      </Fragment>
    );
  }
}

Openings.parseQueryData = (data) => (
  {
    openings: resolve(data, 'organization/openings/results', []).map(o=>{
      o.application_count = resolve(o, 'applications/pagination/total',0);
      delete o.applications;
      return o;
    }),
    closedOpenings: resolve(data, 'organization/closedOpenings/results', []).map(o=>{
      o.application_count = resolve(o, 'applications/pagination/total',0);
      delete o.applications;
      return o;
    })
  }
);

Openings.query = (token) => (
  HiringQuery(`
    {
      organization{
        openings (limit:100, open:true) {
          results{
            id,
            name,
            open,
            applications (status: "Applied") {
              pagination {
                total
              }
            }
            settings {
              invitation_token
            }
          }
        }
        closedOpenings: openings (limit:100, open:false) {
          results{
            id,
            name,
            open,
            applications (status: "Applied") {
              pagination {
                total
              }
            }
            settings {
              invitation_token
            }
          }
        }
      }
    }
  `)
);

Openings.propTypes = {
  match: PropTypes.object,
  graph_data: PropTypes.object,
  graph_loading: PropTypes.bool,
  reload_graph: PropTypes.func,
  children: PropTypes.object,
  subscription: PropTypes.object
};

export default withConfig(withData(Openings));
